import { useState } from 'react';
import { Search, BookOpen, RefreshCw, Edit3, Save, X, Loader2 } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchJson, postJson, putJson } from '../lib/api';
import { Badge } from '../components/shared/Badge';
import { EmptyState } from '../components/shared/EmptyState';
import { cn } from '../lib/cn';

interface DossierViewProps {
  onNavigate: (view: string, params?: Record<string, string>) => void;
}

interface DossierSection {
  key: string;
  title: string;
  content: string;
  confidence?: number;
}

interface Dossier {
  id: string;
  entityType: string;
  entityId: string;
  entityName: string;
  sections: DossierSection[];
  generatedAt: string;
  updatedAt: string;
}

interface EntityOption {
  id: string;
  name: string;
  type: 'contact' | 'organization';
}

const sectionOrder = [
  'overview',
  'professional_background',
  'interaction_history',
  'insurance_context',
  'social_presence',
  'key_relationships',
  'recent_activity',
];

const sectionTitles: Record<string, string> = {
  overview: 'Overview',
  professional_background: 'Professional Background',
  interaction_history: 'Interaction History',
  insurance_context: 'Insurance Context',
  social_presence: 'Social Presence',
  key_relationships: 'Key Relationships',
  recent_activity: 'Recent Activity',
};

function confidenceColor(score?: number): string {
  if (score == null) return 'gray';
  if (score >= 0.8) return 'green';
  if (score >= 0.5) return 'yellow';
  return 'red';
}

export function DossierView({ onNavigate }: DossierViewProps) {
  const queryClient = useQueryClient();
  const [searchText, setSearchText] = useState('');
  const [selectedEntity, setSelectedEntity] = useState<EntityOption | null>(null);
  const [editingSection, setEditingSection] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');

  // Search entities
  const { data: searchResults } = useQuery({
    queryKey: ['dossier-search', searchText],
    queryFn: () =>
      fetchJson<{ results: EntityOption[] }>(`/api/search?q=${encodeURIComponent(searchText)}&types=contact,organization&limit=10`),
    enabled: searchText.length >= 2,
  });

  // Fetch dossier
  const { data: dossier, isLoading } = useQuery({
    queryKey: ['dossier', selectedEntity?.type, selectedEntity?.id],
    queryFn: () =>
      fetchJson<Dossier>(`/api/dossiers/${selectedEntity!.type}/${selectedEntity!.id}`),
    enabled: !!selectedEntity,
  });

  // Generate dossier
  const generateMutation = useMutation({
    mutationFn: (entity: EntityOption) =>
      postJson<Dossier>(`/api/dossiers/${entity.type}/${entity.id}/generate`, {}),
    onSuccess: () => {
      if (selectedEntity) {
        queryClient.invalidateQueries({
          queryKey: ['dossier', selectedEntity.type, selectedEntity.id],
        });
      }
    },
  });

  // Update section
  const updateSectionMutation = useMutation({
    mutationFn: ({ sectionKey, content }: { sectionKey: string; content: string }) =>
      putJson(`/api/dossiers/${selectedEntity!.type}/${selectedEntity!.id}/sections/${sectionKey}`, { content }),
    onSuccess: () => {
      if (selectedEntity) {
        queryClient.invalidateQueries({
          queryKey: ['dossier', selectedEntity.type, selectedEntity.id],
        });
      }
      setEditingSection(null);
    },
  });

  function handleSelectEntity(entity: EntityOption) {
    setSelectedEntity(entity);
    setSearchText('');
  }

  function handleStartEdit(section: DossierSection) {
    setEditingSection(section.key);
    setEditContent(section.content);
  }

  function handleSaveEdit(sectionKey: string) {
    updateSectionMutation.mutate({ sectionKey, content: editContent });
  }

  return (
    <div className="flex-1 overflow-y-auto">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Dossiers</h1>
          <p className="mt-0.5 text-sm text-slate-500">In-depth profiles for contacts and organizations</p>
        </div>
      </div>

      {/* Search / Select */}
      <div className="border-b border-surface-200 bg-white px-6 py-3">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search for a contact or organization..."
            className="input w-full pl-9"
          />
          {searchResults && searchResults.results.length > 0 && searchText.length >= 2 && (
            <div className="absolute z-20 mt-1 w-full rounded-lg border border-surface-200 bg-white py-1 shadow-lg">
              {searchResults.results.map((entity) => (
                <button
                  key={`${entity.type}-${entity.id}`}
                  onClick={() => handleSelectEntity(entity)}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-surface-50"
                >
                  <Badge color={entity.type === 'contact' ? 'blue' : 'purple'} size="sm">
                    {entity.type}
                  </Badge>
                  <span className="text-slate-700">{entity.name}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {selectedEntity && (
          <div className="mt-2 flex items-center gap-2">
            <span className="text-sm text-slate-500">Viewing:</span>
            <Badge color={selectedEntity.type === 'contact' ? 'blue' : 'purple'}>
              {selectedEntity.type}
            </Badge>
            <span className="text-sm font-medium text-slate-700">{selectedEntity.name}</span>
            <button
              onClick={() => setSelectedEntity(null)}
              className="ml-2 rounded p-0.5 text-slate-400 hover:bg-surface-100 hover:text-slate-600"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-6">
        {!selectedEntity ? (
          <EmptyState
            icon={BookOpen}
            title="Select an entity"
            description="Search for a contact or organization above to view or generate their dossier."
          />
        ) : isLoading ? (
          <div className="animate-pulse space-y-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-32 rounded-lg bg-surface-200" />
            ))}
          </div>
        ) : dossier ? (
          <div className="space-y-4">
            {/* Header with actions */}
            <div className="flex items-center justify-between">
              <div className="text-xs text-slate-400">
                Generated {new Date(dossier.generatedAt).toLocaleDateString()}
              </div>
              <button
                className="btn-secondary btn-sm"
                onClick={() => generateMutation.mutate(selectedEntity)}
                disabled={generateMutation.isPending}
              >
                {generateMutation.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <RefreshCw className="h-3.5 w-3.5" />
                )}
                Refresh
              </button>
            </div>

            {/* Sections */}
            {sectionOrder.map((key) => {
              const section = dossier.sections.find((s) => s.key === key);
              if (!section) return null;
              const isEditing = editingSection === key;

              return (
                <div key={key} className="card p-4">
                  <div className="mb-2 flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-slate-700">
                      {sectionTitles[key] ?? section.title}
                    </h3>
                    <div className="flex items-center gap-2">
                      {section.confidence != null && (
                        <Badge color={confidenceColor(section.confidence)} size="sm">
                          {Math.round(section.confidence * 100)}% confidence
                        </Badge>
                      )}
                      {!isEditing && (
                        <button
                          onClick={() => handleStartEdit(section)}
                          className="btn-ghost btn-sm p-1"
                        >
                          <Edit3 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </div>

                  {isEditing ? (
                    <div className="space-y-2">
                      <textarea
                        value={editContent}
                        onChange={(e) => setEditContent(e.target.value)}
                        rows={6}
                        className="input w-full resize-y"
                      />
                      <div className="flex justify-end gap-2">
                        <button
                          className="btn-secondary btn-sm"
                          onClick={() => setEditingSection(null)}
                        >
                          Cancel
                        </button>
                        <button
                          className="btn-primary btn-sm"
                          onClick={() => handleSaveEdit(key)}
                          disabled={updateSectionMutation.isPending}
                        >
                          <Save className="h-3.5 w-3.5" />
                          Save
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p className="whitespace-pre-wrap text-sm text-slate-600 leading-relaxed">
                      {section.content}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-surface-100">
              <BookOpen className="h-8 w-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-semibold text-slate-700">No dossier yet</h3>
            <p className="mt-1 text-sm text-slate-500">
              Generate a dossier to create an in-depth profile.
            </p>
            <button
              className="btn-primary mt-4"
              onClick={() => generateMutation.mutate(selectedEntity)}
              disabled={generateMutation.isPending}
            >
              {generateMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <BookOpen className="h-4 w-4" />
              )}
              Generate Dossier
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
